import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExpenseService } from '@/services/expense.service';
import { ExpenseAnalytics as ExpenseAnalyticsData, ExpenseCategoryLabels, ExpenseStatusLabels } from '@/models/expense.model';
import { format, startOfMonth, endOfMonth } from 'date-fns';

export const ExpenseAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<ExpenseAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');
  const [monthlyTrend, setMonthlyTrend] = useState<{ month: string; amount: number; count: number }[]>([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const now = new Date();
        let startDate: Date;
        const endDate = endOfMonth(now);

        switch (period) {
          case 'month':
            startDate = startOfMonth(now);
            break;
          case 'quarter': {
            const quarter = Math.floor(now.getMonth() / 3);
            startDate = new Date(now.getFullYear(), quarter * 3, 1);
            break;
          }
          case 'year':
            startDate = new Date(now.getFullYear(), 0, 1);
            break;
        }

        const [analyticsData, trendData] = await Promise.all([
          ExpenseService.getExpenseAnalytics(startDate, endDate, period === 'month' ? 'month' : period === 'quarter' ? 'quarter' : 'year'),
          ExpenseService.getMonthlySpendingTrend(12),
        ]);

        setAnalytics(analyticsData);
        setMonthlyTrend(trendData);
      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [period]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Calculate max for trend chart
  const maxTrendAmount = Math.max(...monthlyTrend.map(t => t.amount), 1);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link to="/expenses" className="text-blue-600 hover:text-blue-700 text-sm">
          ← Back to Expenses
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Expense Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Analyze spending patterns and trends
          </p>
        </div>
        <div className="flex space-x-2">
          {(['month', 'quarter', 'year'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                period === p
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {analytics && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm font-medium text-gray-500">Total Expenses</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.totalExpenses}</p>
              <p className="text-sm text-gray-500 mt-1">expenses submitted</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm font-medium text-gray-500">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(analytics.totalAmount)}</p>
              <p className="text-sm text-gray-500 mt-1">total spending</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm font-medium text-gray-500">Average Expense</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(analytics.averageExpenseAmount)}</p>
              <p className="text-sm text-gray-500 mt-1">per expense</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm font-medium text-gray-500">Compliance Rate</p>
              <p className={`text-3xl font-bold mt-2 ${analytics.policyCompliance.complianceRate >= 90 ? 'text-green-600' : analytics.policyCompliance.complianceRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                {formatPercent(analytics.policyCompliance.complianceRate)}
              </p>
              <p className="text-sm text-gray-500 mt-1">policy adherence</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Spending by Category */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Spending by Category</h2>
              <div className="space-y-4">
                {analytics.byCategory.slice(0, 8).map((cat) => (
                  <div key={cat.category}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{ExpenseCategoryLabels[cat.category] || cat.category}</span>
                      <span className="font-medium">{formatCurrency(cat.amount)} ({cat.count})</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${cat.percentOfTotal}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status Breakdown */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">By Status</h2>
              <div className="space-y-4">
                {Object.entries(analytics.byStatus)
                  .filter(([, data]) => data.count > 0)
                  .sort((a, b) => b[1].amount - a[1].amount)
                  .map(([status, data]) => {
                    const percent = analytics.totalAmount > 0 ? (data.amount / analytics.totalAmount) * 100 : 0;
                    const statusColors: Record<string, string> = {
                      draft: 'bg-gray-500',
                      submitted: 'bg-blue-500',
                      pending_approval: 'bg-yellow-500',
                      approved: 'bg-green-500',
                      rejected: 'bg-red-500',
                      pending_payment: 'bg-purple-500',
                      paid: 'bg-emerald-500',
                      cancelled: 'bg-gray-400',
                    };
                    return (
                      <div key={status}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{ExpenseStatusLabels[status as keyof typeof ExpenseStatusLabels] || status}</span>
                          <span className="font-medium">{formatCurrency(data.amount)} ({data.count})</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className={`${statusColors[status] || 'bg-gray-500'} h-3 rounded-full transition-all duration-300`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Spending Trend</h2>
            <div className="flex items-end justify-between h-64 gap-2">
              {monthlyTrend.map((item) => {
                const height = maxTrendAmount > 0 ? (item.amount / maxTrendAmount) * 100 : 0;
                const monthLabel = format(new Date(item.month + '-01'), 'MMM');
                return (
                  <div key={item.month} className="flex-1 flex flex-col items-center">
                    <div className="flex-1 w-full flex items-end justify-center">
                      <div
                        className="w-full max-w-12 bg-blue-600 rounded-t hover:bg-blue-700 transition-colors cursor-pointer relative group"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      >
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                          {formatCurrency(item.amount)} ({item.count} expenses)
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 mt-2">{monthLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Spenders */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Spenders</h2>
              <div className="space-y-4">
                {analytics.byEmployee.slice(0, 5).map((emp, idx) => (
                  <div key={emp.employeeId} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{emp.employeeName}</p>
                        <p className="text-xs text-gray-500">{emp.count} expenses</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(emp.amount)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Department */}
            {analytics.byDepartment.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">By Department</h2>
                <div className="space-y-4">
                  {analytics.byDepartment.slice(0, 5).map((dept) => (
                    <div key={dept.departmentId} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{dept.departmentName}</p>
                        <p className="text-xs text-gray-500">{dept.count} expenses</p>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(dept.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approval Metrics */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Approval Metrics</h2>
              <dl className="space-y-4">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Avg. Approval Time</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {analytics.approvalMetrics.avgApprovalTime.toFixed(1)} hours
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Pending Expenses</dt>
                  <dd className="text-sm font-medium text-yellow-600">
                    {analytics.approvalMetrics.pendingCount} ({formatCurrency(analytics.approvalMetrics.pendingAmount)})
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Rejection Rate</dt>
                  <dd className={`text-sm font-medium ${analytics.approvalMetrics.rejectionRate <= 5 ? 'text-green-600' : analytics.approvalMetrics.rejectionRate <= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {formatPercent(analytics.approvalMetrics.rejectionRate)}
                  </dd>
                </div>
                <div className="border-t pt-4 mt-4">
                  <dt className="text-sm text-gray-500 mb-2">Policy Violations</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {analytics.policyCompliance.totalViolations} total
                    {analytics.policyCompliance.violationsByType && Object.keys(analytics.policyCompliance.violationsByType).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {Object.entries(analytics.policyCompliance.violationsByType).map(([type, count]) => (
                          <div key={type} className="flex justify-between text-xs">
                            <span className={type === 'error' ? 'text-red-600' : type === 'warning' ? 'text-yellow-600' : 'text-blue-600'}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </span>
                            <span>{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Projects */}
          {analytics.byProject.length > 0 && (
            <div className="mt-6 bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">By Project</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Billable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {analytics.byProject.map((project) => (
                      <tr key={project.projectId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{project.projectName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">{project.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">{formatCurrency(project.amount)}</td>
                        <td className="px-4 py-3 text-sm text-green-600 text-right">{formatCurrency(project.billableAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
